/* eslint-disable indent */
import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  CircularProgress
} from '@mui/material';

import {
  InputType,
  DagSection,
  keyValueApi,
  dagTriggered,
  ConnectorInfo,
  CustomSetting
} from 'types/types';
import {
  getFalseState,
  validateSalesforceSettings,
  validateRepliconSettings,
  validateVantagepointSettings,
  validateLaborCode
} from 'utils/validateSettings';
import { postRequest } from 'utils/helper';
import { useMeContext } from 'modules/me';
import { ToastSnackBar } from '../Setting/ToastSnackBar';
import { DropdownSection } from './DropdownSection';
import { MultipleInputFieldSection } from './MultipleInputFieldSection';
import { TextfieldSection } from './TextfieldSection';
import { CheckboxSection } from './CheckboxSection';
import { DatefieldSection } from './DatefieldSection';
import { LaborCodeSection } from './LaborCodeSection';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(0.15),
    paddingTop: theme.spacing(2)
  },
  headingContainer: { marginBottom: theme.spacing(2.5) },
  heading: {
    color: theme.palette.text.primary,
    fontSize: theme.spacing(3)
  },
  headingDescription: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: theme.spacing(1.4),
    lineHeight: theme.spacing(2.3),
    marginRight: theme.spacing(1.7)
  },
  RangeCriteriaKey: { display: 'flex', flex: 0.5 },
  RangeCriteriaValue: { flex: 0.15 },
  triggerIndicator: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(6.5)
  },
  mandatoryField: {
    color: theme.palette.error.main
  },
  buttonContainer: {
    padding: theme.spacing(2, 0)
  }
}));

type selectTextFieldPropsProps = {
  info: ConnectorInfo;
  dagId: string;
  dag: DagSection;
  initialCustomSetting: any;
  setDagSettings: React.Dispatch<React.SetStateAction<any>>;
  setDagHistorySaved: React.Dispatch<React.SetStateAction<boolean>>;
  dagSwitch?: string;
  isPendingEnable?: boolean;
  setIsPendingEnable?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCustomSettingsSaved?: React.Dispatch<React.SetStateAction<boolean>>;
};
export const MultipleCustomSettings: React.FC<selectTextFieldPropsProps> = ({
  info,
  dagId,
  dag,
  initialCustomSetting,
  setDagSettings,
  setDagHistorySaved,
  dagSwitch,
  isPendingEnable = false,
  setIsPendingEnable,
  setIsCustomSettingsSaved
}: selectTextFieldPropsProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const me = useMeContext();
  const initialValue = getFalseState(initialCustomSetting);
  const [changedFields, setChangedFields] = React.useState(initialValue);
  const [invalidFields, setInvalidFields] = React.useState(initialValue);
  const [customSetting, setCustomSetting] = useState({
    customSetting: initialCustomSetting,
    dagId: dagId
  });
  const [validationState, setValidationState] = useState<string>('success');
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [
    isPendingEnableDialogOpen,
    setIsPendingEnableDialogOpen
  ] = useState<boolean>(false);

  const mandatorySettings = useMemo(() => {
    if (!dag || !Array.isArray(dag.customSettings)) return [];

    return dag.customSettings
      .filter((setting) =>
        info.shortName === 'replicon'
          ? setting?.mandatory
          : setting?.required === undefined || setting?.required === true
      )
      .map((setting) => setting?.id as string);
  }, [dag, info.shortName]);

  const [save, setSave] = useState(initialCustomSetting);

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setDagSettings(save);
    setIsSnackBarOpen(true);
  }, [save, setDagSettings]);

  const handleEnableClick = useCallback(
    async (customSettings: any) => {
      try {
        const body = {
          connectorName: info.shortName,
          companyKey: info.companyKey,
          dagId: dagId,
          customSettings: customSettings,
          key: keyValueApi.dagSettings
        };
        const res = await postRequest(
          '/integration-settings-api/dag-configuration',
          body,
          false
        );
        if (res.data) {
          setCustomSetting(res.data);
          if (res.data.customSettingsSaved) {
            setIsCustomSettingsSaved?.(true);
          }
          if (res.data.pendingEnable) {
            setIsPendingEnable?.(true);
            setIsPendingEnableDialogOpen(true);
          }
        }
      } catch (error: any) {
        if (error.response) {
          setValidationState('error');
        }
      }
    },
    [
      dagId,
      info.companyKey,
      info.shortName,
      setIsPendingEnable,
      setIsCustomSettingsSaved
    ]
  );

  const handleDagTrigger = useCallback(
    async (customSettings: any, dagTriggered: any) => {
      try {
        const body = {
          connectorName: info.shortName,
          companyKey: info.companyKey,
          dagId: dagId,
          customSettings: { ...customSettings, useruri: me?.id }
        };
        const res = await postRequest(
          '/integration-settings-api/trigger-ondemand-report',
          body,
          false
        );
        if (res.data) {
          dagTriggered.dagEcid = res.data.dagEcid;
        }
      } catch (error: any) {
        if (error.response) {
          setValidationState('error');
        }
      }
    },
    [dagId, info.companyKey, info.shortName, me?.id]
  );

  const dagRunHistoryDetails = useCallback(
    (dagTriggered: dagTriggered, triggerDate: string) => {
      return {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        integrationType: dagId,
        dagrunDetails: {
          dagId: dagId,
          dagDescription: dagId,
          dagrunId: dagTriggered.dagEcid,
          dagrunStatus: 'executing',
          dagrunExecutionDate: triggerDate,
          dagrunQueuedAt: triggerDate,
          dagrunStartedAt: triggerDate,
          dagrunCompletedAt: triggerDate,
          ecid: dagTriggered.dagEcid,
          errorMessage: 'null'
        }
      };
    },
    [dagId, info.shortName, info.companyKey]
  );

  const pushDagrunHistory = useCallback(
    async (dagTriggered: dagTriggered) => {
      try {
        const triggerDate = new Date().toISOString();
        const body = dagRunHistoryDetails(dagTriggered, triggerDate);
        await postRequest(
          '/integration-settings-api/dagrun-history',
          body,
          true
        );
        setDagHistorySaved((state) => !state);
      } catch (error: any) {
        if (error.response) {
          setValidationState('error');
        }
      }
    },
    [setDagHistorySaved, dagRunHistoryDetails]
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleYes = () => {
    setOpen(false);
    triggerReportDag();
  };

  const triggerReportDag = React.useCallback(async () => {
    const dagTriggered = { dagEcid: '' };
    setLoading(true);
    await handleDagTrigger(save, dagTriggered);
    await pushDagrunHistory(dagTriggered);
    setLoading(false);
    onSnackbarOpen();
  }, [handleDagTrigger, pushDagrunHistory, onSnackbarOpen, save]);

  const handleSaveButton = React.useCallback(async () => {
    const currentState = getFalseState(save);
    setChangedFields(currentState);

    const validateSettings = (connectorName: string, currentSettings: any) => {
      switch (connectorName) {
        case 'salesforce':
          return validateSalesforceSettings(
            dagId,
            currentSettings,
            invalidFields,
            setInvalidFields,
            mandatorySettings
          );
        case 'replicon':
          return validateRepliconSettings(
            currentSettings,
            mandatorySettings,
            setInvalidFields
          );
        case 'vantagepoint': {
          const vpResult = validateVantagepointSettings(
            currentSettings,
            setInvalidFields
          );
          if (!vpResult.includes('success')) return vpResult;
          if (dagId === 'initial_setup') {
            const laborCodeError = validateLaborCode(
              currentSettings.laborCodeSetting
            );
            if (laborCodeError) {
              setInvalidFields((prev: any) => ({
                ...prev,
                laborCodeSetting: true
              }));
              return formatMessage({ id: laborCodeError });
            }
          }
          return 'dagCustomSettings.toastSnackbar.successMessage';
        }
        default:
          return 'dagCustomSettings.toastSnackbar.successMessage';
      }
    };

    const validationResult = validateSettings(info.shortName, save);
    setValidationState(validationResult);
    if (!validationResult.includes('success')) {
      onSnackbarOpen();
      return;
    }
    setInvalidFields(currentState);

    await handleEnableClick(save);
    if (info.shortName === 'replicon') {
      setOpen(true);
    } else {
      onSnackbarOpen();
    }
  }, [
    dagId,
    info.shortName,
    save,
    handleEnableClick,
    invalidFields,
    mandatorySettings,
    onSnackbarOpen,
    formatMessage
  ]);

  const getButtonText = useCallback(
    (connectorName) => {
      const messageId =
        connectorName === 'replicon'
          ? Array.isArray(dag.customSettings)
            ? 'repliconReportConnector.saveAndExecute'
            : 'repliconReportConnector.execute'
          : 'dagCustomSettings.save';
      return <FormattedMessage id={messageId} />;
    },
    [dag.customSettings]
  );

  const alertMessage = React.useMemo(() => {
    const message =
      validationState === 'error'
        ? 'dagCustomSettings.toastSnackbar.errorMessage'
        : validationState;

    return formatMessage({ id: message });
  }, [formatMessage, validationState]);

  const handleSettingValue = useCallback(
    (id: string) => {
      return initialCustomSetting[id];
    },
    [initialCustomSetting]
  );

  const dagCustomSetting = useCallback(
    (inputSetting: CustomSetting) => {
      const settingId = inputSetting.id as string;
      const commonProps = {
        inputSetting: inputSetting,
        initialSelectValue: handleSettingValue(settingId),
        setSave: setSave
      };
      switch (inputSetting.type) {
        case InputType.Dropdown:
          return <DropdownSection {...commonProps} />;

        case InputType.MultipleInputField:
        case InputType.RangeCriteriaSelection:
          return (
            <MultipleInputFieldSection
              {...commonProps}
              changedFields={changedFields}
              isFieldInvalid={invalidFields[settingId]}
              setChangedFields={setChangedFields}
              setInvalidFields={setInvalidFields}
              disabled={isPendingEnable}
            />
          );

        case InputType.TextField:
          return (
            <TextfieldSection
              {...commonProps}
              setChangedFields={setChangedFields}
              isFieldChanged={changedFields[settingId]}
              isFieldInvalid={invalidFields[settingId]}
              isValidationRequired={info.shortName !== 'replicon'}
            />
          );
        case InputType.Checkbox:
          return <CheckboxSection {...commonProps} />;

        case InputType.Date:
          return <DatefieldSection {...commonProps} />;

        case InputType.LaborCode:
          return (
            <LaborCodeSection
              initialSelectValue={handleSettingValue(settingId)}
              setSave={setSave}
              isFieldInvalid={invalidFields[settingId]}
              setInvalidFields={setInvalidFields}
              settingId={settingId}
            />
          );
      }
    },
    [
      handleSettingValue,
      changedFields,
      invalidFields,
      isPendingEnable,
      info.shortName
    ]
  );

  return (
    <div className={classes.container} data-testid="add-setting">
      {dag &&
        Array.isArray(dag.customSettings) &&
        dag.customSettings
          .filter(
            (x) =>
              x.id !== 'statusSyncCriteria' ||
              (x.id === 'statusSyncCriteria' && save['toSyncStatus'])
          )
          .map((inputSetting: CustomSetting) => (
            <div key={inputSetting.id}>
              {inputSetting.heading && (
                <div className={classes.headingContainer}>
                  <Typography className={classes.heading}>
                    {inputSetting.heading}
                  </Typography>
                  {inputSetting.description && (
                    <div className={classes.headingDescription}>
                      <span>{inputSetting.description}</span>
                    </div>
                  )}
                </div>
              )}
              <Typography data-testid="check-title">
                {inputSetting.id === 'statusSyncCriteria' &&
                save['statusSyncCriteria']?.length ? (
                  <div className={classes.RangeCriteriaKey}>
                    <span className={classes.RangeCriteriaValue}>
                      Probability
                    </span>
                    <span>Project Status</span>
                  </div>
                ) : (
                  inputSetting.title
                )}
                {inputSetting.mandatory && (
                  <span className={classes.mandatoryField}>*</span>
                )}
              </Typography>
              <Box
                component="form"
                autoComplete="on"
                data-testid="check-dropdown-textfield"
              >
                {dagCustomSetting(inputSetting)}
              </Box>
            </div>
          ))}
      <div>
        {loading ? (
          <>
            <CircularProgress
              size={22}
              data-testid="connect-progress"
              className={classes.triggerIndicator}
            />
          </>
        ) : (
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              value={customSetting as any}
              onClick={handleSaveButton}
              disabled={isPendingEnable}
              startIcon={
                isPendingEnable ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              data-testid="save-button"
            >
              {getButtonText(info.shortName)}
            </Button>
          </div>
        )}
        <Dialog open={open} onClose={handleClose} data-testid="alert-dialog">
          <DialogTitle data-testid="alert-dialog-title">
            <FormattedMessage
              id={'repliconReportConnector.triggerConfirmation.title'}
            />
          </DialogTitle>
          <DialogContent>
            <DialogContentText data-testid="alert-dialog-message">
              <FormattedMessage
                id={'repliconReportConnector.triggerConfirmation.helpText'}
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} data-testid="no-button">
              <FormattedMessage
                id={'repliconReportConnector.triggerConfirmation.disagree'}
              />
            </Button>
            <Button onClick={handleYes} autoFocus data-testid="yes-button">
              <FormattedMessage
                id={'repliconReportConnector.triggerConfirmation.agree'}
              />
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <ToastSnackBar
        show={isSnackBarOpen}
        category={validationState.includes('success') ? 'success' : 'error'}
        message={alertMessage}
        onClose={onSnackbarClose}
      />
      <Dialog
        open={isPendingEnableDialogOpen}
        onClose={() => setIsPendingEnableDialogOpen(false)}
        data-testid="pending-enable-dialog"
      >
        <DialogTitle>
          <FormattedMessage
            id={
              save?.userSyncFilters?.length
                ? 'vantagepointInstallDialog.pendingEnableDialog.title'
                : 'vantagepointInstallDialog.pendingEnableDialog.deleteTitle'
            }
          />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage
              id={
                !save?.userSyncFilters?.length
                  ? 'vantagepointInstallDialog.pendingEnableDialog.deleteContent'
                  : dagSwitch !== 'Enabled'
                  ? 'vantagepointInstallDialog.pendingEnableDialog.content'
                  : 'vantagepointInstallDialog.pendingEnableDialog.updateContent'
              }
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsPendingEnableDialogOpen(false)}
            color="primary"
            autoFocus
            data-testid="pending-enable-ok-button"
          >
            <FormattedMessage
              id={'vantagepointInstallDialog.pendingEnableDialog.okButton'}
            />
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
